import { ContentDocumentView } from '../components/content-document';
import { ErrorPage, LoadingPage } from '../components/resource-state';
import { loadFixtures, loadOrientation, type Manifest, type Orientation } from '../content/data';
import { useResource } from '../content/use-resource';

const guideGroups = [
  {
    title: 'Run the week',
    description: 'The routine, time boxes, retrospectives, and recovery rules.',
    documentIds: [
      'doc-00-orientation-start-here',
      'doc-00-orientation-execution',
      'doc-00-orientation-recovery',
      'doc-00-orientation-retrospectives'
    ]
  },
  {
    title: 'Build the work',
    description: 'Project continuity, repository structure, Git, releases, and demos.',
    documentIds: [
      'doc-00-orientation-project-map',
      'doc-00-orientation-codebase',
      'doc-00-orientation-git-collaboration',
      'doc-00-orientation-deployment',
      'doc-00-orientation-demo-rubric'
    ]
  },
  {
    title: 'Prepare to explain',
    description: 'Interview practice, technology choices, resources, and unfamiliar terms.',
    documentIds: [
      'doc-00-orientation-interview-system',
      'doc-00-orientation-technology-decisions',
      'doc-00-orientation-resources',
      'doc-00-orientation-glossary'
    ]
  }
] as const;

function GuideIndex({ orientation }: { orientation: Orientation }) {
  const byId = new Map(orientation.documents.map((document) => [document.id, document]));
  const featured = new Set<string>(guideGroups.flatMap((group) => [...group.documentIds]));
  const remaining = orientation.documents.filter((document) => !featured.has(document.id));

  return (
    <main className="wide-page" id="main-content">
      <header className="page-heading">
        <h1>Working guides</h1>
        <p>Open these when a decision or schedule problem appears. You do not need to read them all first.</p>
      </header>

      <div className="guide-shelves">
        {guideGroups.map((group) => (
          <section key={group.title}>
            <header>
              <h2>{group.title}</h2>
              <p>{group.description}</p>
            </header>
            <ul>
              {group.documentIds.flatMap((id) => {
                const document = byId.get(id);
                return document ? [
                  <li key={id}>
                    <a href={`#/guide/${id}`}>{document.title}</a>
                    <span>{document.wordCount} words</span>
                  </li>
                ] : [];
              })}
            </ul>
          </section>
        ))}
      </div>

      {remaining.length > 0 ? (
        <details className="reference-shelf">
          <summary>Other package references</summary>
          <ul>
            {remaining.map((document) => (
              <li key={document.id}><a href={`#/guide/${document.id}`}>{document.title}</a></li>
            ))}
          </ul>
        </details>
      ) : null}
    </main>
  );
}

export function GuidesPage() {
  const resource = useResource(loadOrientation, []);
  if (resource.status === 'loading') return <LoadingPage label="Opening the guide shelf" />;
  if (resource.status === 'error') return <ErrorPage error={resource.error} retry={resource.retry} />;
  return <GuideIndex orientation={resource.data} />;
}

export function GuidePage({ manifest, documentId }: { manifest: Manifest; documentId: string }) {
  const resource = useResource(loadOrientation, []);
  if (resource.status === 'loading') return <LoadingPage label="Opening the guide" />;
  if (resource.status === 'error') return <ErrorPage error={resource.error} retry={resource.retry} />;
  const document = resource.data.documents.find((candidate) => candidate.id === documentId);

  if (!document) {
    return (
      <main className="state-page" id="main-content">
        <h1>Guide not found</h1>
        <a href="#/guides">Return to the guide shelf</a>
      </main>
    );
  }

  return (
    <main className="reading-page guide-page" id="main-content">
      <a className="back-link" href="#/guides">Back to working guides</a>
      <header className="page-heading">
        <h1>{document.title}</h1>
        <p>{document.wordCount} words from the handbook source</p>
      </header>
      <ContentDocumentView document={document} manifest={manifest} />
    </main>
  );
}

export function FixturePage({ filename }: { filename: string }) {
  const resource = useResource(loadFixtures, []);
  if (resource.status === 'loading') return <LoadingPage label="Opening the practice fixture" />;
  if (resource.status === 'error') return <ErrorPage error={resource.error} retry={resource.retry} />;
  const fixture = resource.data.fixtures.find((candidate) => candidate.filename === filename);

  if (!fixture) {
    return (
      <main className="state-page" id="main-content">
        <h1>Fixture not found</h1>
        <p>This fixture may belong to a week that is not loaded in the current build.</p>
        <a href="#/today">Return to today</a>
      </main>
    );
  }

  return (
    <main className="reading-page fixture-page" id="main-content">
      <a className="back-link" href="#/today">Back to today</a>
      <header className="page-heading">
        <p>Practice fixture</p>
        <h1>{fixture.filename}</h1>
        <span>{fixture.language}</span>
      </header>
      <pre><code className={`language-${fixture.language}`}>{fixture.content}</code></pre>
    </main>
  );
}
